import { Divider, TextField } from "@mui/material";
import {
  CircleNotchIcon,
  MapPinIcon,
  MapPinLineIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslations } from "use-intl";

import { ResetButton } from "@/components/ResetButton/ResetButton";
import {
  SearchResultButton,
  SearchResultList,
} from "@/components/SearchResultList/SearchResultList";
import { useHotelsCitiesSearch } from "@/features/hotels/hooks/useHotelsCitiesSearch";
import { initialHotelSearchFormValues } from "@/features/hotels/utils/initialHotelsValues";
import { TopLocationList } from "@/features/search/components/TopLocationList/TopLocationList";
import { useInputAutoFocus } from "@/hooks/useInputAutoFocus";
import { usePreviousCitySearches } from "@/hooks/usePreviousSearches";
import { topDestinationsQueryOptions } from "@/hooks/useTopLocations";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";
import type { City } from "@/types/types";

import { LastSearchedCityList } from "./LastSearchedCityList";

type Props = {
  value: City | null;
  onChange: (value: City | null) => void;
  analyticsSource: SearchSource;
  onClose: () => void;
  inSwipeableDrawer?: boolean;
};

export function DestinationSearch({
  value: destination,
  onChange: setValue,
  analyticsSource,
  onClose,
  inSwipeableDrawer,
}: Props) {
  const t = useTranslations();
  const { previousSearches, addPreviousSearch } = usePreviousCitySearches();
  const { data: topLocations } = useQuery(topDestinationsQueryOptions());
  const [input, setInput] = useState(destination?.name || "");
  const [isDirty, setIsDirty] = useState(input !== destination?.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, isSuccess } = useHotelsCitiesSearch(
    isDirty ? input : "",
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget.value.slice(0, 30);
    setIsDirty(true);
    setInput(input);
  };

  const handleClick = (value: City) => {
    setValue(value);
    onClose();
  };

  const resetDestination = () => {
    setValue(initialHotelSearchFormValues.destination);
    setInput("");
    inputRef.current?.focus();
  };
  const hasPreviousSearches = previousSearches.length > 0;

  return (
    <div className="w-full">
      <div className="relative flex w-full flex-col gap-2">
        <TextField
          name="destination"
          autoFocus={useInputAutoFocus({
            inputRef,
            isInSwipeableDrawer: !!inSwipeableDrawer,
          })}
          autoComplete="off"
          fullWidth
          value={input}
          onChange={onChange}
          onFocus={(e) => {
            e.currentTarget.select();
          }}
          slotProps={{
            input: {
              inputRef,
              endAdornment: destination ? (
                <ResetButton
                  onClick={resetDestination}
                  aria-label={t("hotels.search.resetDestination")}
                />
              ) : null,
            },
          }}
          label={
            <div className="flex items-center gap-2">
              <MapPinLineIcon size={24} />
              <span>{t("hotels.search.destination")}</span>
            </div>
          }
        />
      </div>
      {isFetching ? (
        <div className="mt-6 flex justify-center text-sm text-text-placeholder">
          <CircleNotchIcon className="animate-spin" size={30} />
        </div>
      ) : isSuccess ? (
        data?.length > 0 ? (
          <SearchResultList<City>
            data={data}
            getItemKey={(city) => city.id}
            renderItem={(city) => (
              <SearchResultButton
                icon={MapPinIcon}
                primaryText={city.name}
                secondaryText={city.countryName}
                onClick={() => {
                  addPreviousSearch(city);
                  handleClick(city);
                }}
              />
            )}
          />
        ) : (
          <div className="mt-6 text-center">
            <div className="mb-1 text-base font-medium">
              {t("hotels.search.nothingFound")}
            </div>
            <div className="text-sm text-text-placeholder">
              {t("hotels.search.checkInput")}
            </div>
          </div>
        )
      ) : (
        <>
          {hasPreviousSearches ? (
            <LastSearchedCityList
              onClick={(city) => {
                rudderAnalytics.track(
                  HotelSearchTrackingEvents.USER_SELECTED_HOTEL_RECENT_DESTINATION,
                  {
                    source: analyticsSource,
                    location_id: city.id,
                    location_name: city.name,
                    location_type: city.type,
                  },
                );
                handleClick(city);
              }}
            />
          ) : null}

          {topLocations?.length ? (
            <>
              {hasPreviousSearches ? (
                <Divider
                  sx={{
                    mt: "calc(var(--spacing) * 4)",
                    borderColor: "var(--color-stroke-neutral-soft)",
                  }}
                />
              ) : null}
              <TopLocationList<City>
                topLocations={topLocations}
                onClick={(city) => {
                  rudderAnalytics.track(
                    HotelSearchTrackingEvents.USER_SELECTED_HOTEL_TOP_DESTINATION,
                    {
                      source: analyticsSource,
                      location_id: city.id,
                      location_name: city.name,
                      location_type: city.type,
                    },
                  );
                  handleClick(city);
                }}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
